export const detectBroker = (headers) => {
  const h = headers.map(x => x.toLowerCase().trim());

  if (h.includes('trade date') && h.includes('tradingsymbol'))
    return 'zerodha';
  if (h.includes('trade date') && h.includes('scrip name'))
    return 'upstox';
  if (h.includes('trade date') && h.includes('symbol'))
    return 'angelone';

  return 'unknown';
};

// ZERODHA FORMAT
// Columns: trade date, trade time, settlement id,
// exchange, tradingsymbol, trade type, quantity, price
export const parseZerodha = (rows, headers) => {
  return rows.map(row => {
    const r = {};
    headers.forEach((h, i) => r[h.trim()] = row[i]?.trim());

    const isBuy = r['trade type']?.toUpperCase() === 'BUY';
    const qty = parseInt(r['quantity']) || 0;
    const price = parseFloat(r['price']) || 0;

    return {
      symbol: r['tradingsymbol']?.replace(/[^a-zA-Z]/g, '') || '',
      date: parseDate(r['trade date']),
      time: r['trade time'] || '',
      type: isBuy ? 'BUY' : 'SELL',
      quantity: qty,
      price: price,
      exchange: r['exchange'] || 'NSE',
      broker: 'Zerodha',
      imported: true,
    };
  }).filter(t => t.symbol && t.date);
};

// UPSTOX FORMAT
// Columns: Trade Date, Trade Time, Security/Contract
// Description, Buy/Sell, Quantity, Trade Price
export const parseUpstox = (rows, headers) => {
  return rows.map(row => {
    const r = {};
    headers.forEach((h, i) => r[h.trim()] = row[i]?.trim());

    const isBuy = r['buy/sell']?.toUpperCase() === 'BUY' ||
                  r['b/s']?.toUpperCase() === 'B';

    return {
      symbol: extractSymbol(
        r['security/contract description'] ||
        r['scrip name'] || ''
      ),
      date: parseDate(r['trade date']),
      time: r['trade time'] || '',
      type: isBuy ? 'BUY' : 'SELL',
      quantity: parseInt(r['quantity']) || 0,
      price: parseFloat(r['trade price'] || r['price']) || 0,
      exchange: 'NSE',
      broker: 'Upstox',
      imported: true,
    };
  }).filter(t => t.symbol && t.date);
};

// ANGEL ONE FORMAT
// Columns: Trade Date, Symbol, Trade Type,
// Quantity, Price, Net Amount
export const parseAngelOne = (rows, headers) => {
  return rows.map(row => {
    const r = {};
    headers.forEach((h, i) => r[h.trim()] = row[i]?.trim());

    const isBuy = r['trade type']?.toUpperCase() === 'BUY' ||
                  r['buy/sell']?.toUpperCase() === 'BUY';

    return {
      symbol: r['symbol']?.replace(/[^a-zA-Z]/g, '') || '',
      date: parseDate(r['trade date']),
      time: r['trade time'] || '09:15',
      type: isBuy ? 'BUY' : 'SELL',
      quantity: parseInt(r['quantity']) || 0,
      price: parseFloat(r['price'] || r['trade price']) || 0,
      exchange: r['exchange'] || 'NSE',
      broker: 'Angel One',
      imported: true,
    };
  }).filter(t => t.symbol && t.date);
};

// Match BUY and SELL trades to calculate P&L
export const matchTrades = (rawTrades) => {
  const grouped = {};

  rawTrades.forEach(trade => {
    const key = `${trade.symbol}_${trade.date}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(trade);
  });

  const completedTrades = [];

  Object.values(grouped).forEach(trades => {
    const buys = trades.filter(t => t.type === 'BUY');
    const sells = trades.filter(t => t.type === 'SELL');

    if (buys.length > 0 && sells.length > 0) {
      const buyQty = buys.reduce((s,t) => s + t.quantity, 0);
      const sellQty = sells.reduce((s,t) => s + t.quantity, 0);
      const buyAvg = buys.reduce((s,t) =>
        s + (t.price * t.quantity), 0) / buyQty;
      const sellAvg = sells.reduce((s,t) =>
        s + (t.price * t.quantity), 0) / sellQty;

      const qty = Math.min(buyQty, sellQty);
      const pnl = Math.round((sellAvg - buyAvg) * qty);

      completedTrades.push({
        ...buys[0],
        entryPrice: Math.round(buyAvg * 100) / 100,
        exitPrice: Math.round(sellAvg * 100) / 100,
        quantity: qty,
        pnl: pnl,
        emotion: 'Calm',
        setup: 'Imported',
        notes: `Imported from ${buys[0].broker}`,
      });
    }
  });

  return completedTrades;
};

// Helper: Parse various date formats
const parseDate = (dateStr) => {
  if (!dateStr) return null;

  // DD-MM-YYYY or DD/MM/YYYY (Zerodha)
  const dmy = dateStr.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`;
  }

  // YYYY-MM-DD
  const ymd = dateStr.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (ymd) return dateStr;

  // DD-Mon-YYYY (01-Jan-2026)
  const months = {jan:'01',feb:'02',mar:'03',apr:'04',
    may:'05',jun:'06',jul:'07',aug:'08',
    sep:'09',oct:'10',nov:'11',dec:'12'};
  const dmy2 = dateStr.match(/(\d{1,2})[-/\s]([a-zA-Z]+)[-/\s](\d{4})/);
  if (dmy2) {
    const m = months[dmy2[2].toLowerCase().slice(0,3)];
    if (m) return `${dmy2[3]}-${m}-${dmy2[1].padStart(2,'0')}`;
  }

  return dateStr;
};

const extractSymbol = (description) => {
  return description.split(' ')[0]
    .replace(/[^a-zA-Z]/g, '')
    .toUpperCase();
};

// RFC 4180-compliant CSV line parser — handles quoted fields with embedded commas and escaped quotes ("")
const parseCSVLine = (line) => {
  const fields = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { fields.push(field.trim()); field = ''; }
      else { field += ch; }
    }
  }
  fields.push(field.trim());
  return fields;
};

export const parseCSV = (text) => {
  const lines = text.split('\n').filter(l => l.trim());
  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());
  const rows = lines.slice(1).map(line => parseCSVLine(line));
  return { headers, rows };
};

const parseGeneric = (rows, headers) => {
  return rows.map(row => {
    const r = {};
    headers.forEach((h, i) => r[h] = row[i]?.trim());

    return {
      symbol: r['symbol'] || r['scrip'] || r['stock'] || '',
      date: parseDate(r['date'] || r['trade date'] || ''),
      type: (r['type'] || r['buy/sell'] || r['side'] || 'BUY')
        .toUpperCase(),
      quantity: parseInt(r['qty'] || r['quantity'] || 0),
      price: parseFloat(r['price'] || r['rate'] || 0),
      broker: 'Unknown',
      imported: true,
    };
  }).filter(t => t.symbol && t.date);
};

export const parseBrokerCSV = (text) => {
  const { headers, rows } = parseCSV(text);
  const broker = detectBroker(headers);

  let rawTrades = [];

  if (broker === 'zerodha') {
    rawTrades = parseZerodha(rows, headers);
  } else if (broker === 'upstox') {
    rawTrades = parseUpstox(rows, headers);
  } else if (broker === 'angelone') {
    rawTrades = parseAngelOne(rows, headers);
  } else {
    // Generic fallback parser
    rawTrades = parseGeneric(rows, headers);
  }

  return {
    broker,
    rawTrades,
    completedTrades: matchTrades(rawTrades),
  };
};
